// Keep only delivery state in storage, never customer or order contents.
const pendingLifetime = 24 * 60 * 60 * 1000;
const deliveries = new Map<string, Promise<boolean>>();

type Client = {
  identify: (properties: Record<string, unknown>) => Promise<unknown>;
  track: (name: string, properties: Record<string, unknown>) => Promise<boolean>;
};

function readyClient(): Promise<Client> {
  if (!document.querySelector('script[src*="klaviyo"]')) {
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://static.klaviyo.com/onsite/js/RnfC9N/klaviyo.js';
    script.onerror = () => script.remove();
    document.head.appendChild(script);
  }
  return new Promise((resolve, reject) => {
    const deadline = Date.now() + 30000;
    const check = () => {
      if (window.klaviyo?.identify && window.klaviyo?.track) {
        resolve(window.klaviyo as unknown as Client);
      } else if (Date.now() >= deadline) {
        reject(new Error('Klaviyo did not load'));
      } else {
        setTimeout(check, 100);
      }
    };
    check();
  });
}

export function deliverKlaviyoPurchase(
  ticket: any,
  store: string,
  firstVisit: boolean,
  payload: Record<string, unknown>,
): Promise<boolean> {
  const id = `${store}:${ticket.ticket_id || ticket.order_number}`;
  const key = `klaviyo_purchase_v1:${id}`;
  const existing = deliveries.get(key);
  if (existing) return existing;
  let pending = false;
  try {
    const state = JSON.parse(localStorage.getItem(key) || 'null');
    if (state?.status === 'sent') return Promise.resolve(true);
    pending = state?.status === 'pending' && Date.now() - state.at < pendingLifetime;
  } catch {
    /* Storage can be disabled; the event ID still deduplicates delivery. */
  }
  // Do not turn historical confirmation-page visits into new conversions.
  if (!firstVisit && !pending) return Promise.resolve(false);
  try {
    localStorage.setItem(key, JSON.stringify({ status: 'pending', at: Date.now() }));
  } catch {
    /* Best effort. */
  }

  const delivery = (async () => {
    try {
      const client = await readyClient();
      if (ticket.customer_email) {
        await client.identify({
          email: ticket.customer_email,
          first_name: ticket.customer_first_name || '',
          last_name: ticket.customer_last_name || '',
        });
      }
      // A stable store-scoped ID also protects against duplicate tabs/retries.
      const accepted = await client.track('Placed Order', { ...payload, $event_id: id });
      if (accepted !== true) throw new Error('Klaviyo did not accept the event');
      try {
        localStorage.setItem(key, JSON.stringify({ status: 'sent', at: Date.now() }));
      } catch {
        /* Best effort. */
      }
      return true;
    } catch {
      console.warn('[Klaviyo] Purchase delivery incomplete; retry on confirmation revisit');
      deliveries.delete(key);
      return false;
    }
  })();
  deliveries.set(key, delivery);
  return delivery;
}
